import { Button } from "@/design-system/Button/Button";
import { ContentTag } from "@/design-system/Tags/ContentTag/ContentTag";
import { useStartClubImport } from "@/features/MyClub/EmptyClub/useStartClubImport";
import { BREAKPOINTS } from "@/settings";
import { useTranslation } from "@repo/shared/i18n";
import { Check } from "lucide-react";
import { ComponentProps } from "react";
import { Helmet } from "react-helmet-async";
import { useMediaQuery } from "usehooks-ts";

import { MyClubSidePanel } from "../Layout/MyClubSidePanel";

const PITCH_ART = `${import.meta.env.VITE_APP_ASSETS_BASE_URL}/misc/pitch.svg`;

const POINTS = ["bestSquad", "upgrades", "sbcs", "profit", "gallery"] as const;

/* The right column's cards, as they will read once there is a club. */
const PREVIEW_CARDS: {
  id: "upgrade" | "evolution" | "sbc";
  tag: ComponentProps<typeof ContentTag>["contentType"];
}[] = [
  { id: "upgrade", tag: "market" },
  { id: "evolution", tag: "evolution" },
  { id: "sbc", tag: "sbc" },
];

/**
 * My Club before a club is imported: the club home's own layout, with the
 * pitch explaining what an import gives and the side cards shown as a
 * blurred preview of what comes after. One thing to do: import.
 */
export const MyClubLanding = ({
  helmetTitle,
  helmetDescription,
  helmetCanonicalLink,
}: {
  helmetTitle: string;
  helmetDescription?: string;
  helmetCanonicalLink?: string;
}) => {
  const { t } = useTranslation("myClub");
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);
  const startClubImport = useStartClubImport({
    section: "My Club",
    subsection: "Landing",
  });

  return (
    <>
      <Helmet>
        <title>{helmetTitle}</title>
        {helmetDescription && (
          <meta name="description" content={helmetDescription} />
        )}
        <meta property="og:title" content={helmetTitle} />
        {helmetCanonicalLink && (
          <link rel="canonical" href={helmetCanonicalLink} />
        )}
      </Helmet>

      <div className="min-h-screen bg-neutral-900 text-neutral-100">
        <div className="mx-auto flex min-h-screen w-full max-w-content items-stretch">
          {/* The club's side panel as it will be, softened and out of reach:
              a hint of the page to come, not something to use yet. */}
          {!isMobile && (
            <aside
              inert
              aria-hidden
              className="sticky top-12 h-[calc(100dvh-3rem)] w-59 flex-none overflow-hidden border-r border-neutral-800 bg-streamline-surface select-none"
            >
              <div className="h-full opacity-50 blur-xs">
                <MyClubSidePanel />
              </div>
            </aside>
          )}
          <main className="flex min-w-0 flex-1 flex-col gap-8 px-4 py-6 touch-device:flex-row touch-device:items-start touch-device:gap-10 touch-device:px-10 touch-device:py-10">
            {/* The pitch, empty, carrying the pitch for importing. Square from tablets
              up; on phones it grows with its text so nothing is cut off. */}
            <section className="relative mx-auto w-full max-w-150 overflow-hidden mobile:aspect-square rounded-2xl border border-neutral-700 bg-neutral-900 touch-device:max-w-[min(50rem,calc(100dvh-8rem))] touch-device:flex-1">
              <img
                src={PITCH_ART}
                alt=""
                className="absolute inset-0 size-full object-cover opacity-40"
              />
              <div className="absolute inset-0 bg-linear-to-b from-neutral-950/30 via-neutral-950/60 to-neutral-950/90" />
              <div className="relative flex h-full flex-col items-center justify-center gap-5 px-6 py-10 text-center mobile:gap-6 mobile:px-12 mobile:py-0">
                <span className="text-xs font-bold tracking-widest text-blue-300 uppercase">
                  {t("landing.eyebrow")}
                </span>
                <h1 className="font-oswald text-3xl leading-tight font-semibold tracking-wide uppercase mobile:text-5xl">
                  {t("landing.title")}
                </h1>
                <p className="max-w-md text-sm leading-relaxed text-neutral-300 mobile:text-base">
                  {t("landing.body")}
                </p>
                <ul className="flex flex-col gap-2 text-left text-sm text-neutral-200">
                  {POINTS.map((point) => (
                    <li key={point} className="flex items-center gap-2">
                      <Check
                        className="size-4 flex-none text-upgrade"
                        strokeWidth={2.5}
                      />
                      <span>{t(`landing.points.${point}`)}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  variant="primary"
                  size="large"
                  onClick={() => startClubImport()}
                >
                  {t("landing.cta")}
                </Button>
                <span className="text-xs text-foreground-muted">
                  {t("landing.free")}
                </span>
              </div>
            </section>

            {/* What the side panel will hold, softened: a look, not content. */}
            <section
              aria-hidden
              className="flex flex-col gap-3 touch-device:w-96 touch-device:flex-none"
            >
              <div className="flex flex-col gap-1">
                <h2 className="font-oswald text-2xl leading-tight font-semibold tracking-wide uppercase">
                  {t("home.tryNext.title")}
                </h2>
                <span className="text-sm text-neutral-300">
                  {t("landing.previewSubtitle")}
                </span>
              </div>
              <div className="pointer-events-none flex flex-col gap-3 opacity-60 blur-xs select-none">
                {PREVIEW_CARDS.map(({ id, tag }) => (
                  <article
                    key={id}
                    className="flex flex-col gap-3 rounded-xl border border-neutral-700 bg-neutral-800 p-4"
                  >
                    <div className="flex">
                      <ContentTag contentType={tag} />
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="h-16 w-12 flex-none rounded-lg bg-neutral-700" />
                      <div className="flex min-w-0 flex-col gap-1">
                        <span className="text-sm font-semibold text-neutral-50">
                          {t(`landing.preview.${id}.title`)}
                        </span>
                        <span className="text-xs leading-snug text-foreground-muted">
                          {t(`landing.preview.${id}.body`)}
                        </span>
                      </div>
                    </div>
                    <div className="flex justify-end">
                      <span className="h-8 w-28 rounded-full bg-neutral-700" />
                    </div>
                  </article>
                ))}
              </div>
            </section>
          </main>
        </div>
      </div>
    </>
  );
};
